import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FranchiseData, SalonData } from '../../utils/franchiseStorage';

/**
 * Salon Redux Slice
 * Manages salon and franchise state
 */

// ============ State Interface ============

export interface SalonState {
  domain: string | null;
  salon: SalonData | null;
  franchises: FranchiseData[];
  selectedFranchise: FranchiseData | null;
  loading: boolean;
  error: string | null;
}

// ============ Initial State ============

const initialState: SalonState = {
  domain: null,
  salon: null,
  franchises: [],
  selectedFranchise: null,
  loading: false,
  error: null,
};

// ============ Slice ============

const salonSlice = createSlice({
  name: 'salon',
  initialState,
  reducers: {
    // Fetch salon by domain
    fetchSalonByDomain: (state) => {
      state.loading = true;
      state.error = null;
    },
    
    fetchSalonByDomainSuccess: (
      state,
      action: PayloadAction<{
        domain: string;
        salon: SalonData;
        franchises: FranchiseData[];
      }>
    ) => {
      state.loading = false;
      state.domain = action.payload.domain;
      state.salon = action.payload.salon;
      state.franchises = action.payload.franchises;
      state.error = null;
    },
    
    fetchSalonByDomainFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    
    // Select franchise
    selectFranchise: (state, action: PayloadAction<FranchiseData>) => {
      state.selectedFranchise = action.payload;
    },
    
    // Restore from localStorage
    restoreSalonInfo: (
      state,
      action: PayloadAction<{
        salon: SalonData;
        franchise: FranchiseData;
      }>
    ) => {
      state.salon = action.payload.salon;
      state.selectedFranchise = action.payload.franchise;
      state.franchises = [action.payload.franchise];
    },
    
    // Clear salon data
    clearSalonData: (state) => {
      state.domain = null;
      state.salon = null;
      state.franchises = [];
      state.selectedFranchise = null;
      state.error = null;
    },
    
    // Set domain
    setDomain: (state, action: PayloadAction<string>) => {
      state.domain = action.payload;
    },
  },
});

// ============ Actions ============

export const {
  fetchSalonByDomain,
  fetchSalonByDomainSuccess,
  fetchSalonByDomainFailure,
  selectFranchise,
  restoreSalonInfo,
  clearSalonData,
  setDomain,
} = salonSlice.actions;

// ============ Selectors ============

export const selectSalon = (state: { salon: SalonState }) => state.salon.salon;
export const selectFranchises = (state: { salon: SalonState }) => state.salon.franchises;
export const selectSelectedFranchise = (state: { salon: SalonState }) => state.salon.selectedFranchise;
export const selectSalonLoading = (state: { salon: SalonState }) => state.salon.loading;
export const selectSalonError = (state: { salon: SalonState }) => state.salon.error;
export const selectDomain = (state: { salon: SalonState }) => state.salon.domain;

// ============ Export ============

export default salonSlice.reducer;
